import React, { useState, useEffect, useMemo } from "react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {
  getStockTransferByBranch,
  getIncomingStockTransfers,
  acceptStockTransfer,
  deleteStockTransfer,
} from "../../services/StockApiServices";
import { useSelector } from "react-redux";
import StockTransferModal from "../../components/transactions/modal/StockTransferModal";
import { InvStockTransfer } from "../../core/json/InvStockTransfer";
import { TransactionScreen } from "../../components/transactions/list";
import { dateOf, dmy, intg, num } from "../../components/report/reportFormat";

const STATUS = { InTransit: "In transit", Accepted: "Accepted" };

const StockTransfer = () => {
  const [updateData, setUpdateData] = useState([]);
  const [modalMode, setModalMode] = useState("add");
  const [modalOpen, setModalOpen] = useState(false);
  const [stockList, setStockList] = useState([]);
  // transfers sent to this branch (in transit until accepted here)
  const [incomingList, setIncomingList] = useState([]);

  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);
  const [permit, setpermit] = useState();
  useEffect(() => {
    setpermit(
      menuItems?.menuPermissions?.find((e) => e.menu.name == "Stock Transfer")
    );
  }, []);

  useEffect(() => {
    handlestockData();
  }, [branch]);

  const handlestockData = async () => {
    try {
      const response = await getStockTransferByBranch(branch);
      const stockData = Array.isArray(response) ? response : [];
      setStockList(stockData);
    } catch (error) {
      console.error(
        "Failed to fetch Stock adjustments",
        error?.response?.data?.Message || "something went wrong"
      );
    }
    try {
      const incoming = await getIncomingStockTransfers(branch);
      setIncomingList(Array.isArray(incoming) ? incoming : []);
    } catch (error) {
      console.error("Failed to fetch incoming transfers", error?.response?.data?.message || error?.message);
    }
  };

  const MySwal = withReactContent(Swal);

  // incoming transfers belong to the sending branch: only accepting is done here
  const isIncoming = (r) => r?.direction === "in";
  const notHere = () =>
    MySwal.fire({
      title: "Incoming transfer",
      text: "Only the sending branch can change or delete a transfer.",
      icon: "info",
      confirmButtonText: "OK",
    });

  const handleAccept = (r) => {
    MySwal.fire({
      title: "Accept this transfer?",
      text: `${r.billNo} from ${r.fromBranch}: the stock comes into ${branch?.name} today.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Accept",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await acceptStockTransfer({ guid: r.guid, branchID: branch?.guid, updatedUser: userId?.guid || userId?.id });
        MySwal.fire({ title: "Accepted", text: "The stock is now in this branch.", icon: "success", confirmButtonText: "OK" });
        handlestockData();
      } catch (error) {
        MySwal.fire({
          title: "Error!",
          text: error?.response?.data?.message || error?.response?.data?.Message || "something went wrong",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    });
  };

  // Handle the click event to delete a Stock adjustments
  const handleDeleteClick = (obj) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00ff00",
      cancelButtonColor: "#ff0000",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const objStockTrn = new InvStockTransfer();
          objStockTrn.id = obj?.id;
          objStockTrn.guid = obj?.guid;
          objStockTrn.branch = branch;
          objStockTrn.isDeleted = true;
          objStockTrn.updatedUser = `${userId?.id}`;
          const response = await deleteStockTransfer(objStockTrn);
          if (response?.data === true) {
            MySwal.fire({
              title: "Deleted!",
              text: "The Stock Transfer has been deleted.",
              icon: "success",
              confirmButtonColor: "#00ff00",
              confirmButtonText: "OK",
            }).then(() => {
              handlestockData();
            });
          } else {
            MySwal.fire({
              title: "Error!",
              text: "Something went wrong.",
              icon: "error",
              confirmButtonColor: "#ff0000",
              confirmButtonText: "OK",
            });
          }
        } catch (error) {
          console.error(
            "Error deleting reason:",
            error?.response?.data?.Message || "something went wrong"
          );
          MySwal.fire({
            title: "Error!",
            text: error?.response?.data?.Message || "something went wrong",
            icon: "error",
            confirmButtonColor: "#ff0000",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleEditClick = async (guid) => {
    try {
      const response = getStockTrnByGuid(guid);
      setUpdateData(response);
      setModalMode("edit");
      setModalOpen(true);
    } catch (error) {
      console.error(
        "Error fetching Stock adjustment by GUID",
        error?.response?.data?.Message || "something went wrong"
      );
    }
  };

  function getStockTrnByGuid(guid) {
    const filteredStock = stockList?.filter(
      (stockTrn) => stockTrn.guid === guid
    );

    return filteredStock[0];
  }

  /* ---------------- list ---------------- */
  // one flat record per transfer; the keys match the columns
  const rows = useMemo(
    () => [
      ...stockList.map((entry) => {
        const lines = entry?.invStockTransferDetails || [];
        return {
          guid: entry.guid,
          raw: entry,
          direction: "out",
          date: dateOf(entry?.date),
          billNo: entry?.invoiceNo || "",
          fromBranch: branch?.name || "",
          toBranch: entry?.toBranch?.name || "",
          toBranchGuid: entry?.toBranch?.guid,
          status: entry?.status || "Accepted",
          remarks: entry?.remarks || "",
          items: lines.length,
          qty: lines.reduce((a, l) => a + num(l?.quantity), 0),
          lines,
        };
      }),
      ...incomingList.map((entry) => {
        const lines = (entry?.lines || []).map((l) => ({
          // FOR JSON keeps the column names as they are
          product: { name: l?.ProductName },
          batch: l?.Batch,
          quantity: l?.Quantity,
        }));
        return {
          guid: entry.guID || entry.guid,
          raw: entry,
          direction: "in",
          date: dateOf(entry?.date),
          billNo: entry?.invoiceno || "",
          fromBranch: entry?.branchName || "",
          toBranch: entry?.toBranchName || "",
          toBranchGuid: entry?.toBranchID,
          status: entry?.status || "Accepted",
          remarks: entry?.remarks || "",
          items: lines.length,
          qty: num(entry?.quantity),
          lines,
        };
      }),
    ],
    [stockList, incomingList, branch]
  );

  const columns = useMemo(
    () => [
      { k: "date", t: "Date", type: "date", ago: true },
      {
        k: "billNo",
        t: "Entry No",
        type: "doc",
        badge: (r) => (r.status === "InTransit" ? { text: "IN TRANSIT", tone: "pos" } : null),
      },
      { k: "fromBranch", t: "From Branch" },
      { k: "toBranch", t: "To Branch" },
      { k: "status", t: "Status", cell: (r) => STATUS[r.status] || r.status },
      { k: "remarks", t: "Remarks" },
      { k: "items", t: "Items", type: "int", off: true },
      { k: "qty", t: "Quantity", type: "int", off: true },
    ],
    []
  );

  const tabs = useMemo(
    () => [
      { id: "out", label: "Sent", match: (r) => r.direction === "out" },
      { id: "in", label: "Incoming", match: (r) => r.direction === "in" },
      { id: "transit", label: "To accept", match: (r) => r.direction === "in" && r.status === "InTransit" },
    ],
    []
  );

  // the branches this branch has transferred to
  const filters = useMemo(() => {
    const seen = new Map();
    stockList.forEach((e) => {
      const g = e?.toBranch?.guid;
      if (g && !seen.has(g)) seen.set(g, e?.toBranch?.name || "—");
    });
    return [
      {
        key: "toBranches",
        label: "To Branch",
        items: [...seen].map(([v, t]) => ({ v, t })),
        match: (r, v) => v.includes(r.toBranchGuid),
      },
    ];
  }, [stockList]);

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.toBranch) ||
      hit(r.remarks) ||
      hit(r.date) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code) || hit(l?.batch))
    );
  };

  const drawerContent = (r) => ({
    title: isIncoming(r) ? `From ${r.fromBranch}` : r.toBranch ? `To ${r.toBranch}` : "Stock transfer",
    subtitle: [r.billNo, dmy(r.date)].filter(Boolean).join("  ·  "),
    avatarKey: (isIncoming(r) ? r.fromBranch : r.toBranch) || r.billNo,
    meta: [
      { k: "From branch", v: r.fromBranch },
      { k: "To branch", v: r.toBranch },
      { k: "Status", v: STATUS[r.status] || r.status },
      ...(r.raw?.acceptedDate ? [{ k: "Accepted", v: dmy(dateOf(r.raw.acceptedDate)) }] : []),
      { k: "Date", v: dmy(r.date) },
      { k: "Remarks", v: r.remarks },
      { k: "Items", v: intg(r.items) },
    ],
    lines: {
      title: "Transfer lines",
      columns: [{ t: "Item" }, { t: "Batch" }, { t: "Qty", align: "right" }],
      rows: r.lines.map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">{[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}</div>
        </>,
        l?.batch || "—",
        intg(l?.quantity),
      ]),
    },
    totals: [{ l: "Total quantity", v: intg(r.qty) }],
  });

  return (
    <TransactionScreen
      title="Stock Transfer"
      subtitle="Manage Stock Transfers here"
      unit="transfers"
      rows={rows}
      columns={columns}
      tabs={tabs}
      filters={filters}
      searchMatch={searchMatch}
      searchPlaceholder="Entry no, branch, remarks, item…"
      sortOptions={[
        { v: "date:-1", t: "Newest first" },
        { v: "date:1", t: "Oldest first" },
        { v: "billNo:-1", t: "Entry no (high–low)" },
        { v: "billNo:1", t: "Entry no (low–high)" },
        { v: "toBranch:1", t: "To branch A–Z" },
      ]}
      exportName="Stocktransfer"
      onRefresh={handlestockData}
      canAdd={permit?.add}
      newLabel="New transfer"
      onNew={() => {
        setModalMode("add");
        setModalOpen(true);
      }}
      canEdit={permit?.edit}
      onEdit={(r) => (isIncoming(r) ? notHere() : handleEditClick(r.guid))}
      editLabel="Edit transfer"
      canDelete={permit?.deletion}
      // the old list passed a reshaped row without `id`; kept as it was
      onDelete={(r) => (isIncoming(r) ? notHere() : handleDeleteClick({ guid: r.guid }))}
      drawerContent={drawerContent}
      drawerActions={(r) =>
        isIncoming(r) && r.status === "InTransit"
          ? [{ label: "Accept transfer", primary: true, onClick: handleAccept }]
          : []
      }
      keysDisabled={modalOpen}
    >
      {modalOpen && (
        <StockTransferModal
          mode={modalMode}
          data={modalMode === "edit" ? updateData : {}}
          handleClose={() => setModalOpen(false)}
          handleRefresh={() => {
            handlestockData();
          }}
        />
      )}
    </TransactionScreen>
  );
};

export default StockTransfer;
